// A drilled hole's own page (Ryan, Oct 3 2026): the link from the hole row on the drilling
// card lands here. For now it carries what the row knows — the job, the sheet, the Fieldwire
// task and its pictures, the hole's sizes and status. Ryan will say what else belongs here.
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { apiFetch, API_BASE } from '../../services/apiClient';
import { FieldwireTaskDrawer } from './FieldwirePanel';
import { AttachThumb } from '../../components/AttachButton';

const photoUrl = fn => `${API_BASE.replace('/api', '')}/uploads/${fn}`;
const HOLE_STATUS = { pending: ['Pending', '#6b7280', '#f3f4f6'], drilled: ['Drilled', '#1d4ed8', '#eff6ff'], done: ['Done', '#15803d', '#f0fdf4'] };
const lbl = { fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 };

export default function AdminHoleDetail() {
  const { holeId } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(undefined);   // { hole, sub_job, project, fw_task, fw_statuses }
  const [task, setTask] = useState(null);
  const [drawer, setDrawer] = useState(false);
  useEffect(() => { apiFetch(`/holes/${holeId}`).then(setD).catch(() => setD(null)); }, [holeId]);
  useEffect(() => {
    if (!d || !d.hole || !d.hole.fw_task_id || !d.project) { setTask(null); return; }
    apiFetch(`/projects/${d.project.id}/fieldwire/tasks/${d.hole.fw_task_id}`).then(setTask).catch(() => setTask(null));
  }, [d]);
  const back = () => (d && d.sub_job ? navigate(`/admin/workflows/${d.sub_job.type || 'vertical_drilling'}?card=${d.sub_job.id}`) : navigate(-1));
  if (d === undefined) return <AdminLayout><div style={{ color: '#9ca3af', padding: 30 }}>Loading…</div></AdminLayout>;
  if (!d || !d.hole) return <AdminLayout><div style={{ padding: 30 }}>That hole isn’t here any more.</div></AdminLayout>;
  const { hole: h, sub_job: sj, project: p, fw_task: ft } = d;
  const st = HOLE_STATUS[h.status] || HOLE_STATUS.pending;
  const fwPhotos = (task && task.photos) || [];
  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <button onClick={back} style={{ background: 'none', border: 'none', padding: 0, color: '#6b7280', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 4 }}>← {sj ? (sj.name || 'Drilling card') : 'Back'}</button>
          <h1 className="admin-page-title">🕳️ Hole {h.label || ''}</h1>
          <p className="admin-page-sub">
            {p ? <><button onClick={() => navigate(`/admin/jobs/${p.id}`)} style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontWeight: 800, cursor: 'pointer', font: 'inherit' }}>{p.project_number}</button> · {p.name}</> : null}
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 18 }}>
        {[['Sheet', ft && ft.sheet ? `📄 ${ft.sheet}` : '—'], ['Fieldwire task', ft && ft.number ? `📍 #${ft.number}${ft.status ? ' · ' + ft.status : ''}` : (h.fw_task_id ? '📍 linked' : 'Not linked')], ['Depth', h.depth || '—'], ['Diameter', h.diameter ? `Ø ${h.diameter}` : '—']].map(([k, v]) => (
          <div key={k} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px' }}>
            <div style={lbl}>{k}</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{v}</div>
          </div>
        ))}
        <div style={{ background: st[2], border: `1px solid ${st[1]}33`, borderRadius: 12, padding: '12px 14px' }}>
          <div style={lbl}>Status</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: st[1] }}>{st[0]}</div>
        </div>
      </div>

      {h.note && <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', marginBottom: 18, fontSize: 14, whiteSpace: 'pre-wrap' }}>{h.note}</div>}

      {h.fw_task_id && (
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={lbl}>📍 On the Fieldwire task{task && task.sequence_number ? ` #${task.sequence_number}` : ''}{task && task.name ? ` — ${task.name}` : ''}</div>
            <span style={{ flex: 1 }} />
            <button onClick={() => setDrawer(true)} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1e40af', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Open the task</button>
          </div>
          {!task ? <div style={{ fontSize: 13, color: '#9ca3af' }}>Reading Fieldwire…</div>
            : fwPhotos.length === 0 ? <div style={{ fontSize: 13, color: '#9ca3af' }}>No pictures on the task yet.</div>
            : <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{fwPhotos.map(x => <AttachThumb key={x.id} size={96} src={x.thumb_url || x.file_url} name={x.file_name || 'photo'} href={x.file_url} />)}</div>}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 18 }}>
        <div style={lbl}>In the app · {(h.photos || []).length}</div>
        {(h.photos || []).length === 0 ? <div style={{ fontSize: 13, color: '#9ca3af' }}>No pictures in the app for this hole.</div>
          : <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{h.photos.map(x => <AttachThumb key={x.id} size={96} src={photoUrl(x.filename)} name={x.original_name || x.filename} href={photoUrl(x.filename)} />)}</div>}
      </div>

      <div style={{ fontSize: 12.5, color: '#9ca3af' }}>This page is the start of the hole's detail — say what else belongs on it.</div>

      {drawer && p && <FieldwireTaskDrawer projectId={p.id} taskId={h.fw_task_id} statuses={d.fw_statuses || []} canEdit onClose={() => setDrawer(false)} onChanged={() => apiFetch(`/holes/${holeId}`).then(setD).catch(() => {})} />}
    </AdminLayout>
  );
}
